import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { organizationsApi, companyTeamApi } from '../lib/api';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { LogOut, User, Languages, Globe, Users, AlertTriangle, CreditCard, Settings, Building2, Palette, DollarSign, Inbox, Plug, Library, Eye, List, Coins, LayoutDashboard, ChevronDown, Check, Loader2, Bell, Mail, FileText, Menu, Scale, Megaphone, Upload, Sparkles, ShieldCheck, Network } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { ImpersonateModal } from './ImpersonateModal';

import { API_BASE } from '../lib/apiBase';
const API = API_BASE || '';

import { LANGUAGE_NAMES } from '../lib/languageNames';

// Company role badge styling
const getRoleBadgeClass = (role) => {
    const styles = {
        owner: 'bg-purple-100 text-purple-700',
        admin: 'bg-blue-100 text-blue-700',
        member: 'bg-gray-100 text-gray-600',
        viewer: 'bg-gray-100 text-gray-500',
        customer: 'bg-green-100 text-green-700',
    };
    return styles[role] || styles.member;
};

/**
 * Renders a company logo image when `logoUrl` is set, otherwise falls
 * back to a generic Building2 icon. Kept small so it can be used inline
 * in the Header (topbar switcher + single-company pill + dropdown rows).
 * The <img> onError handler swaps back to the icon if a data URI ever
 * ends up broken — we never want the topbar to show a broken image.
 *
 * `className` sizes the OUTER bounding box (e.g. "h-8 w-8"). The image
 * itself uses object-contain so wide/tall logos are letterboxed inside
 * without stretching or breaking the header layout.
 */
const CompanyLogo = ({ logoUrl, name, className = '', iconClassName = '', testId }) => {
    const [broken, setBroken] = useState(false);
    if (!logoUrl || broken) {
        return <Building2 className={iconClassName || className} data-testid={testId} />;
    }
    return (
        <span
            className={`${className} inline-flex items-center justify-center overflow-hidden bg-white border border-slate-200`}
        >
            <img
                src={logoUrl}
                alt={name ? `${name} logo` : 'Company logo'}
                className="max-h-full max-w-full object-contain"
                data-testid={testId}
                onError={() => setBroken(true)}
            />
        </span>
    );
};

export const Header = ({ focusMode = false }) => {
    const { user, setUser, logout, isAdmin, isSuperAdmin, canAccessTranslations, isImpersonating, 
            activeCompany, companies, hasMultipleCompanies, switchCompany, switchingCompany } = useAuth();
    const { t, language, setLanguage, supportedLanguages } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [billingStatus, setBillingStatus] = useState(null);
    const [showImpersonateModal, setShowImpersonateModal] = useState(false);
    const [actionsSummary, setActionsSummary] = useState(null);
    // Customer-side badge feed: count of overdue invoices for the user's
    // active company. Mirrors how the admin Finance badge is wired up
    // (60 s polling + `actions-summary-refresh` event) so the customer's
    // Invoices nav shows a red number the moment any sync flips a
    // payment status to "overdue".
    const [customerOverdue, setCustomerOverdue] = useState({ overdue_count: 0 });
    const [hasManageableCompanies, setHasManageableCompanies] = useState(false);
    // Mobile nav drawer. Hamburger button on <md replaces the inline tabs that
    // otherwise overflow the viewport on phones (see screenshot: tabs pushed
    // the logo + user menu off-screen). The drawer holds the same nav items
    // so functionality is preserved.
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    
    // Detect whether user owns/admins any company → show "Team" menu
    useEffect(() => {
        if (!user || focusMode) return;
        companyTeamApi.getManageableCompanies()
            .then(res => setHasManageableCompanies((res.data || []).length > 0))
            .catch(() => setHasManageableCompanies(false));
    }, [user, focusMode]);
    
    // Fetch admin actions summary
    useEffect(() => {
        if (isAdmin && !focusMode) {
            const fetchActionsSummary = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(`${API}/api/admin/actions-summary`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setActionsSummary(response.data);
                } catch (error) {
                    console.error('Failed to fetch actions summary:', error);
                }
            };
            fetchActionsSummary();
            
            // Refresh every 60 seconds
            const interval = setInterval(fetchActionsSummary, 60000);

            // Also refresh on demand when other pages broadcast a refresh
            // event (e.g. after "Sync All Companies" updates org overdue
            // balances). Listening on `window` keeps the wiring loose so
            // any page can trigger it without lifting state.
            const onRefresh = () => fetchActionsSummary();
            window.addEventListener('actions-summary-refresh', onRefresh);

            // Refresh when the tab regains focus — catches the case where
            // an admin ran Sync All in another tab or window and switches
            // back expecting the badge to be current.
            const onVisibility = () => {
                if (document.visibilityState === 'visible') fetchActionsSummary();
            };
            document.addEventListener('visibilitychange', onVisibility);

            return () => {
                clearInterval(interval);
                window.removeEventListener('actions-summary-refresh', onRefresh);
                document.removeEventListener('visibilitychange', onVisibility);
            };
        }
    }, [isAdmin, focusMode]);

    // Customer overdue badge feed — only for customer-role users (skip
    // admins who already see the broader Finance badge). 60 s polling
    // + reactive refresh on `actions-summary-refresh` so a Sync All
    // Companies run flips the badge immediately.
    useEffect(() => {
        if (!user || focusMode) return;
        if (user.role !== 'customer') return;

        const fetchOverdue = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API}/api/invoice-sync/customer/overdue-count`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setCustomerOverdue(res.data || { overdue_count: 0 });
            } catch (e) {
                // Silent fail — badge just stays hidden.
            }
        };
        fetchOverdue();
        const interval = setInterval(fetchOverdue, 60000);
        const onRefresh = () => fetchOverdue();
        window.addEventListener('actions-summary-refresh', onRefresh);
        return () => {
            clearInterval(interval);
            window.removeEventListener('actions-summary-refresh', onRefresh);
        };
    }, [user, focusMode]);

    
    // Handle company switch
    const handleSwitchCompany = async (companyId) => {
        if (companyId === activeCompany?.id || switchingCompany) return;
        try {
            await switchCompany(companyId);
            // Reload to refresh all data with new company context
            window.location.reload();
        } catch (error) {
            console.error('Failed to switch company:', error);
        }
    };
    
    // Check billing profile status for non-admin users (skip in focus mode)
    useEffect(() => {
        if (user?.active_company_id && !isAdmin && !focusMode) {
            organizationsApi.getBillingStatus(user.active_company_id)
                .then(res => {
                    setBillingStatus(res.data);
                })
                .catch(err => {
                    console.error('Failed to check billing status:', err);
                });
        }
    }, [user?.active_company_id, isAdmin, focusMode]);
    
    // Determine warning message based on billing status
    const getBillingWarning = () => {
        if (!billingStatus) return null;
        
        const status = billingStatus.billing_status;
        if (status === 'synced_to_economic') return null;
        
        if (status === 'pending_approval') {
            return { text: t('header.billing_pending', 'Pending Approval'), color: 'bg-blue-50 border-blue-200 text-blue-700' };
        }
        
        return { text: t('header.billing_complete', 'Complete Billing Profile'), color: 'bg-amber-50 border-amber-200 text-amber-700' };
    };
    
    const billingWarning = getBillingWarning();

    const isReproPartner = user?.role === 'repro_partner';
    const isGuest = user?.role === 'guest';
    const homeRoute = isReproPartner ? '/artwork-jobs' : '/dashboard';

    // Guest upload-link sessions get a stripped-down header — no navigation,
    // no company switcher, no admin/team menus.
    if (isGuest) {
        return (
            <header className="border-b border-border bg-white sticky top-0 z-50 h-14 flex items-center px-6" data-testid="app-header-guest">
                <div className="flex items-center gap-3">
                    <img src="/owntune-logo-dark.png" alt="Owntune" className="h-7 w-auto" />
                    <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">
                        {t('header.upload_guest', 'Upload guest')}
                    </span>
                </div>
                <div className="ml-auto flex items-center gap-3">
                    <div className="text-right hidden sm:block">
                        <p className="text-[11px] text-slate-500 leading-tight">{t('header.guest_invited', "You've been invited to upload files")}</p>
                        {user?.active_company_name && (
                            <p className="text-xs font-medium text-slate-700 leading-tight">
                                {t('header.for_company', 'for {company}', { company: user.active_company_name })}
                            </p>
                        )}
                    </div>
                    {/* Compact language selector — guests can pick their language
                        too, persisted anonymously via localStorage. */}
                    {supportedLanguages.length > 1 && (
                        <Select value={language} onValueChange={setLanguage}>
                            <SelectTrigger
                                className="h-8 w-auto min-w-[64px] px-2 gap-1 text-xs border-slate-200 hover:bg-slate-50"
                                data-testid="header-language-selector-guest"
                                aria-label={t('header.language', 'Language')}
                            >
                                <Globe className="h-3.5 w-3.5 text-slate-500" />
                                <span className="font-medium uppercase tracking-wide text-slate-700">
                                    {language}
                                </span>
                            </SelectTrigger>
                            <SelectContent align="end">
                                {supportedLanguages.map(lang => (
                                    <SelectItem
                                        key={lang}
                                        value={lang}
                                        className="text-xs"
                                        data-testid={`header-language-option-guest-${lang}`}
                                    >
                                        <span className="uppercase font-mono tracking-wide text-slate-500 mr-2">{lang}</span>
                                        {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            logout();
                            navigate('/login', { replace: true });
                        }}
                        data-testid="guest-exit-button"
                    >
                        <LogOut className="h-4 w-4 mr-1.5" />
                        {t('header.exit', 'Exit')}
                    </Button>
                </div>
            </header>
        );
    }

    return (
        <header className="glass-header sticky top-0 z-50 h-16 flex items-center px-4 sm:px-6" data-testid="app-header">
            {/* Mobile hamburger — replaces the inline nav tabs on <md so the
                logo + user menu stay visible on phones. The drawer contains
                the same set of nav links/dropdowns. */}
            {!focusMode && (
                <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                    <SheetTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden mr-1 h-9 w-9 flex-shrink-0"
                            data-testid="mobile-nav-trigger"
                        >
                            <Menu className="h-5 w-5" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-72 p-0">
                        <SheetHeader className="p-4 border-b">
                            <SheetTitle className="text-left">{t('header.navigation', 'Navigation')}</SheetTitle>
                        </SheetHeader>
                        <nav className="flex flex-col p-2" data-testid="mobile-nav-list">
                            {isAdmin && !isReproPartner && (
                                <button
                                    onClick={() => { navigate('/admin/overview'); setMobileNavOpen(false); }}
                                    className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-slate-100 text-left text-sm font-medium"
                                    data-testid="mobile-nav-overview"
                                >
                                    <LayoutDashboard className="h-4 w-4 text-slate-500" />
                                    Overview
                                </button>
                            )}
                            {!isReproPartner && (
                                <button
                                    onClick={() => { navigate(isAdmin ? '/admin' : '/dashboard'); setMobileNavOpen(false); }}
                                    className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-slate-100 text-left text-sm font-medium"
                                    data-testid="mobile-nav-dashboard"
                                >
                                    <LayoutDashboard className="h-4 w-4 text-slate-500" />
                                    {isAdmin ? 'Orders' : (location.pathname.startsWith('/orders/') ? t('nav.my_orders', 'My Orders') : t('nav.dashboard', 'Dashboard'))}
                                </button>
                            )}
                            {!isAdmin && !isReproPartner && user?.role === 'customer' && (
                                <button
                                    onClick={() => { navigate('/catalogue'); setMobileNavOpen(false); }}
                                    className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-slate-100 text-left text-sm font-medium"
                                    data-testid="mobile-nav-catalogue"
                                >
                                    <Library className="h-4 w-4 text-slate-500" />
                                    {t('nav.catalogue_library', 'Catalogue Library')}
                                </button>
                            )}
                            {!isAdmin && !isReproPartner && user?.role === 'customer' && (
                                <button
                                    onClick={() => { navigate('/invoices'); setMobileNavOpen(false); }}
                                    className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-slate-100 text-left text-sm font-medium"
                                    data-testid="mobile-nav-invoices"
                                >
                                    <FileText className="h-4 w-4 text-slate-500" />
                                    {t('nav.invoices', 'Invoices')}
                                    {customerOverdue?.overdue_count > 0 && (
                                        <span
                                            className="ml-auto h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[11px] font-bold bg-red-500 text-white rounded-full"
                                            data-testid="mobile-nav-invoices-overdue-badge"
                                        >
                                            {customerOverdue.overdue_count}
                                        </span>
                                    )}
                                </button>
                            )}
                            {(isAdmin || isReproPartner) && (
                                <button
                                    onClick={() => { navigate('/artwork-jobs'); setMobileNavOpen(false); }}
                                    className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-slate-100 text-left text-sm font-medium"
                                    data-testid="mobile-nav-artwork-jobs"
                                >
                                    <Palette className="h-4 w-4 text-slate-500" />
                                    Artwork Jobs
                                </button>
                            )}
                            {isAdmin && (
                                <>
                                    <button
                                        onClick={() => { navigate('/finance/invoices'); setMobileNavOpen(false); }}
                                        className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-slate-100 text-left text-sm font-medium"
                                        data-testid="mobile-nav-finance"
                                    >
                                        <DollarSign className="h-4 w-4 text-slate-500" />
                                        Finance
                                    </button>
                                    <button
                                        onClick={() => { navigate('/admin/customers'); setMobileNavOpen(false); }}
                                        className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-slate-100 text-left text-sm font-medium"
                                        data-testid="mobile-nav-companies"
                                    >
                                        <Users className="h-4 w-4 text-slate-500" />
                                        Customers
                                    </button>
                                    <button
                                        onClick={() => { navigate('/admin/settings'); setMobileNavOpen(false); }}
                                        className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-slate-100 text-left text-sm font-medium"
                                        data-testid="mobile-nav-settings"
                                    >
                                        <Settings className="h-4 w-4 text-slate-500" />
                                        Settings
                                    </button>
                                </>
                            )}
                        </nav>
                    </SheetContent>
                </Sheet>
            )}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <button 
                    onClick={() => navigate(homeRoute)}
                    className="flex items-center hover:opacity-80 transition-opacity flex-shrink-0"
                >
                    <img src="/owntune-logo-dark.png" alt="Owntune" className="h-7 sm:h-8 w-auto" />
                </button>
                {isAdmin && (
                    <span className="hidden sm:inline text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                        Admin
                    </span>
                )}
                {isReproPartner && (
                    <span className="hidden sm:inline text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">
                        Partner
                    </span>
                )}
                {user?.role === 'translator' && (
                    <span className="hidden sm:inline text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                        Translator
                    </span>
                )}
            </div>

            {/* Inline navigation — desktop only. The mobile drawer above
                renders the same items on <md. */}
            {!focusMode && (
            <nav className="ml-8 hidden md:flex items-center gap-1">
                {isAdmin && !isReproPartner && (
                    <Button
                        variant={location.pathname === '/admin/overview' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => navigate('/admin/overview')}
                        data-testid="nav-overview"
                    >
                        <LayoutDashboard className="h-4 w-4 mr-1" />
                        Overview
                    </Button>
                )}
                {!isReproPartner && (
                    <Button
                        variant={location.pathname === '/dashboard' || location.pathname.startsWith('/orders/') || location.pathname === '/admin' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')}
                        data-testid="nav-dashboard"
                        className="relative"
                    >
                        {isAdmin ? 'Orders' : (location.pathname.startsWith('/orders/') ? t('nav.my_orders', 'My Orders') : t('nav.dashboard', 'Dashboard'))}
                        {isAdmin && (actionsSummary?.orders?.missing_artwork > 0 || actionsSummary?.orders?.supplier_errors > 0 || actionsSummary?.orders?.awaiting_approval > 0) && (
                            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 flex items-center justify-center text-[10px] font-bold bg-amber-500 text-white rounded-full">
                                {(actionsSummary.orders.missing_artwork || 0) + (actionsSummary.orders.supplier_errors || 0) + (actionsSummary.orders.awaiting_approval || 0)}
                            </span>
                        )}
                    </Button>
                )}
                {!isAdmin && !isReproPartner && user?.role === 'customer' && (
                    <Button
                        variant={location.pathname === '/catalogue' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => navigate('/catalogue')}
                        data-testid="nav-catalogue"
                    >
                        <Library className="h-4 w-4 mr-1" />
                        {t('nav.catalogue_library', 'Catalogue Library')}
                    </Button>
                )}
                {!isAdmin && !isReproPartner && user?.role === 'customer' && (
                    <Button
                        variant={location.pathname === '/invoices' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => navigate('/invoices')}
                        data-testid="nav-invoices"
                        className="relative"
                    >
                        <FileText className="h-4 w-4 mr-1" />
                        {t('nav.invoices', 'Invoices')}
                        {customerOverdue?.overdue_count > 0 && (
                            <span
                                className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full"
                                title={`${customerOverdue.overdue_count} overdue invoice${customerOverdue.overdue_count === 1 ? '' : 's'}`}
                                data-testid="nav-invoices-overdue-badge"
                            >
                                {customerOverdue.overdue_count}
                            </span>
                        )}
                    </Button>
                )}
                {!isAdmin && !isReproPartner && user?.role === 'customer' && (
                    <Button
                        variant={location.pathname === '/artwork-templates' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => navigate('/artwork-templates')}
                        data-testid="nav-artwork-templates"
                    >
                        <Palette className="h-4 w-4 mr-1" />
                        {t('nav.artwork_templates', 'Artwork Templates')}
                    </Button>
                )}
                {(isAdmin || isReproPartner) && (
                    <Button
                        variant={location.pathname.startsWith('/artwork-jobs') ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => navigate('/artwork-jobs')}
                        data-testid="nav-artwork-jobs"
                    >
                        <Palette className="h-4 w-4 mr-1" />
                        Artwork Jobs
                    </Button>
                )}
                {isAdmin && (
                    <Button
                        variant={location.pathname.startsWith('/admin/customers') || location.pathname === '/users' || location.pathname.startsWith('/admin/companies') ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => navigate('/admin/customers')}
                        data-testid="nav-users"
                        className="relative"
                    >
                        <Users className="h-4 w-4 mr-1" />
                        Customers
                        {actionsSummary?.users?.pending_approvals > 0 && (
                            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full">
                                {actionsSummary.users.pending_approvals}
                            </span>
                        )}
                    </Button>
                )}
                {isAdmin && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant={location.pathname.startsWith('/finance') ? 'secondary' : 'ghost'}
                                size="sm"
                                data-testid="nav-finance"
                                className="relative"
                            >
                                <DollarSign className="h-4 w-4 mr-1" />
                                Finance
                                {actionsSummary?.finance?.companies_with_overdue > 0 && (
                                    <span
                                        className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full"
                                        title={`${actionsSummary.finance.companies_with_overdue} ${actionsSummary.finance.companies_with_overdue === 1 ? 'company has' : 'companies have'} overdue invoices`}
                                        data-testid="nav-finance-overdue-badge"
                                    >
                                        {actionsSummary.finance.companies_with_overdue}
                                    </span>
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            {isSuperAdmin && (
                                <DropdownMenuItem onClick={() => navigate('/finance')} data-testid="nav-finance-dashboard">
                                    <LayoutDashboard className="h-4 w-4 mr-2" />
                                    Dashboard
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => navigate('/finance/invoices')} data-testid="nav-finance-invoices">
                                <FileText className="h-4 w-4 mr-2" />
                                Invoices & Credit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/finance/reconciliation')} data-testid="nav-finance-reconciliation">
                                <Scale className="h-4 w-4 mr-2" />
                                Supplier Reconciliation
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/admin/reports/ncb')} data-testid="nav-ncb-report">
                                <FileText className="h-4 w-4 mr-2" />
                                NCB Report
                            </DropdownMenuItem>
                            {isSuperAdmin && (
                                <DropdownMenuItem onClick={() => navigate('/finance/expenses')} data-testid="nav-operating-expenses">
                                    <CreditCard className="h-4 w-4 mr-2" />
                                    Operating Expenses
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
                {isAdmin && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant={
                                    // Highlight when on any of the Settings dropdown's targets so
                                    // the pill stays lit while the user is deep-inside a settings
                                    // sub-section (e.g. /admin/settings, /admin/pricing).
                                    (
                                        location.pathname.startsWith('/settings')
                                        || location.pathname.startsWith('/admin/settings')
                                        || location.pathname.startsWith('/admin/pricing')
                                        || location.pathname.startsWith('/admin/inbound-documents')
                                        || location.pathname.startsWith('/admin/sony-upload-jobs')
                                        || location.pathname.startsWith('/admin/campaigns')
                                        || location.pathname.startsWith('/admin/visual-builder')
                                        || location.pathname.startsWith('/admin/attribute-lists')
                                        || location.pathname.startsWith('/admin/catalogue-sku-backfill')
                                        || location.pathname.startsWith('/admin/distribution')
                                        || location.pathname.startsWith('/admin/email-log')
                                        || location.pathname.startsWith('/admin/system-logs')
                                        || location.pathname.startsWith('/admin/artwork-templates')
                                        || location.pathname.startsWith('/admin/product-config')
                                    ) ? 'secondary' : 'ghost'
                                }
                                size="sm"
                                data-testid="nav-settings"
                            >
                                <Settings className="h-4 w-4 mr-1" />
                                Admin
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Business</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => navigate('/settings/partners')} data-testid="nav-partners">
                                <Building2 className="h-4 w-4 mr-2" />
                                Partners
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/admin/pricing')} data-testid="nav-pricing">
                                <DollarSign className="h-4 w-4 mr-2" />
                                Pricing
                            </DropdownMenuItem>
                            {/* Companies moved into the top-level
                                Customers hub (2026-02). */}
                            <DropdownMenuItem onClick={() => navigate('/admin/campaigns')} data-testid="nav-campaigns">
                                <Megaphone className="h-4 w-4 mr-2" />
                                Marketing
                            </DropdownMenuItem>
                            {isSuperAdmin && (
                                <DropdownMenuItem onClick={() => navigate('/admin/distribution')} data-testid="nav-distribution-demo">
                                    <Network className="h-4 w-4 mr-2" />
                                    Distribution (Demo)
                                </DropdownMenuItem>
                            )}

                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Operations</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => navigate('/admin/inbound-documents')} data-testid="nav-inbound-documents">
                                <Inbox className="h-4 w-4 mr-2" />
                                Inbound Documents
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => navigate('/admin/sony-upload-jobs')}
                                data-testid="nav-sony-upload-jobs"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Sony Upload Jobs
                                {(actionsSummary?.orders?.sony_upload_failed_or_stale || 0) > 0 && (
                                    <span
                                        className="ml-auto h-4 min-w-[16px] px-1 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full"
                                        title="Failed or stale Sony jobs"
                                    >
                                        {actionsSummary.orders.sony_upload_failed_or_stale}
                                    </span>
                                )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/admin/email-log')} data-testid="nav-email-logs">
                                <Mail className="h-4 w-4 mr-2" />
                                Email Logs
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/admin/system-logs')} data-testid="nav-system-logs">
                                <FileText className="h-4 w-4 mr-2" />
                                System Logs
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Configuration</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => navigate('/admin/visual-builder')} data-testid="nav-visual-builder">
                                <Palette className="h-4 w-4 mr-2" />
                                Visual Builder Assets
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/admin/artwork-templates')} data-testid="nav-artwork-templates-admin">
                                <Palette className="h-4 w-4 mr-2" />
                                Artwork Templates
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/admin/attribute-lists')} data-testid="nav-attribute-lists">
                                <List className="h-4 w-4 mr-2" />
                                Attribute Lists
                            </DropdownMenuItem>
                            {canAccessTranslations && (
                                <DropdownMenuItem onClick={() => navigate('/settings/translations')} data-testid="nav-translations">
                                    <Languages className="h-4 w-4 mr-2" />
                                    Translations
                                </DropdownMenuItem>
                            )}

                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">System</DropdownMenuLabel>
                            {isSuperAdmin && (
                                <DropdownMenuItem onClick={() => navigate('/admin/catalogue-sku-backfill')} data-testid="nav-catalogue-sku-backfill">
                                    <ShieldCheck className="h-4 w-4 mr-2" />
                                    Catalogue SKU Backfill
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => navigate('/admin/settings')} data-testid="nav-system-settings">
                                <Settings className="h-4 w-4 mr-2" />
                                System Settings
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </nav>
            )}

            <div className="ml-auto flex items-center gap-2 sm:gap-4 min-w-0">
                {/* Company Switcher - Show for users with multiple companies */}
                {!focusMode && hasMultipleCompanies && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button 
                                variant="outline" 
                                className="flex items-center gap-2 h-9 px-2 sm:px-3 max-w-[160px] sm:max-w-[280px] lg:max-w-[360px]"
                                data-testid="company-switcher"
                            >
                                <CompanyLogo
                                    logoUrl={activeCompany?.logo_url}
                                    name={activeCompany?.name}
                                    className="h-8 w-8 rounded-md flex-shrink-0"
                                    iconClassName="h-5 w-5 text-slate-500 flex-shrink-0"
                                    testId="active-company-logo"
                                />
                                <span
                                    className="text-sm truncate hidden sm:inline"
                                    title={activeCompany?.name || t('header.select_company', 'Select Company')}
                                >
                                    {activeCompany?.name || t('header.select_company', 'Select Company')}
                                </span>
                                {switchingCompany ? (
                                    <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                                ) : (
                                    <ChevronDown className="h-3 w-3 text-slate-400 flex-shrink-0" />
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64">
                            <div className="px-2 py-1.5">
                                <p className="text-xs font-medium text-slate-500 uppercase">{t('header.switch_company', 'Switch Company')}</p>
                            </div>
                            <DropdownMenuSeparator />
                            {companies.map((company) => (
                                <DropdownMenuItem
                                    key={company.id}
                                    onClick={() => handleSwitchCompany(company.id)}
                                    disabled={switchingCompany || company.id === activeCompany?.id}
                                    className="flex items-center justify-between py-2"
                                    data-testid={`switch-to-${company.id}`}
                                >
                                    <div className="min-w-0 flex-1 flex items-center gap-2">
                                        <CompanyLogo
                                            logoUrl={company.logo_url}
                                            name={company.name}
                                            className="h-6 w-6 rounded flex-shrink-0 border border-slate-200"
                                            iconClassName="h-4 w-4 text-slate-400 flex-shrink-0"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium truncate">{company.name}</span>
                                                {company.is_primary && (
                                                    <span className="text-xs px-1 py-0.5 bg-amber-100 text-amber-700 rounded flex-shrink-0">
                                                        {t('header.default_badge', 'Default')}
                                                    </span>
                                                )}
                                            </div>
                                            <span className={`text-xs px-1.5 py-0.5 rounded inline-block mt-0.5 ${getRoleBadgeClass(company.role)}`}>
                                                {company.role}
                                            </span>
                                        </div>
                                    </div>
                                    {company.id === activeCompany?.id && (
                                        <Check className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" />
                                    )}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
                
                {/* Single Company Display (non-admin with one company).
                    Hidden on mobile — surfaced in the user menu dropdown
                    instead, where it already appears under the user's name. */}
                {!focusMode && !hasMultipleCompanies && !isAdmin && activeCompany?.name && (
                    <div className="hidden md:flex items-center gap-2 pl-1.5 pr-3 py-1 bg-slate-50 rounded-lg border border-slate-200">
                        <CompanyLogo
                            logoUrl={activeCompany.logo_url}
                            name={activeCompany.name}
                            className="h-8 w-8 rounded-md flex-shrink-0"
                            iconClassName="h-5 w-5 text-slate-400"
                            testId="active-company-logo"
                        />
                        <span className="text-sm text-slate-600">{activeCompany.name}</span>
                    </div>
                )}

                {/* Compact language / globe selector — customer, guest & admin.
                    Anonymous selection is persisted in localStorage; authenticated
                    users' choice is PATCHed to /auth/me so it roams across
                    sessions. See TranslationContext.setLanguage for details. */}
                {supportedLanguages.length > 1 && (
                    <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger
                            className="h-8 w-auto min-w-[64px] px-2 gap-1 text-xs border-slate-200 hover:bg-slate-50"
                            data-testid="header-language-selector"
                            aria-label={t('header.language', 'Language')}
                        >
                            <Globe className="h-3.5 w-3.5 text-slate-500" />
                            <span className="font-medium uppercase tracking-wide text-slate-700">
                                {language}
                            </span>
                        </SelectTrigger>
                        <SelectContent align="end">
                            {supportedLanguages.map(lang => (
                                <SelectItem
                                    key={lang}
                                    value={lang}
                                    className="text-xs"
                                    data-testid={`header-language-option-${lang}`}
                                >
                                    <span className="uppercase font-mono tracking-wide text-slate-500 mr-2">{lang}</span>
                                    {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                
                {/* Billing Profile Warning - Hidden in focus mode */}
                {!focusMode && billingWarning && (
                    <button
                        onClick={() => navigate('/billing')}
                        className={`flex items-center gap-2 px-3 py-1.5 border rounded-full text-xs font-medium hover:opacity-80 transition-all ${billingWarning.color}`}
                        data-testid="billing-warning-badge"
                    >
                        <AlertTriangle className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{billingWarning.text}</span>
                        <span className="sm:hidden">{t('header.billing', 'Billing')}</span>
                    </button>
                )}

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button 
                            variant="ghost" 
                            className="flex items-center gap-2 h-9 px-3"
                            data-testid="user-menu-trigger"
                        >
                            {/* Avatar shows the effective profile picture
                                when available (custom upload or Google);
                                falls back to initials on error / missing. */}
                            {user?.picture ? (
                                <img
                                    src={user.picture}
                                    alt={user?.name || 'User'}
                                    className="w-7 h-7 rounded-full object-cover"
                                    data-testid="user-avatar-image"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                            ) : (
                                <div
                                    className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-medium"
                                    data-testid="user-avatar-initials"
                                >
                                    {user?.name?.charAt(0).toUpperCase()}
                                </div>
                            )}
                            <span className="text-sm font-medium hidden sm:inline">{user?.name}</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <div className="px-2 py-1.5">
                            <p className="text-sm font-medium">{user?.name}</p>
                            <p className="text-xs text-muted-foreground">{user?.email}</p>
                            {activeCompany?.name && (
                                <div className="flex items-center gap-1.5 mt-1">
                                    <Building2 className="h-3 w-3 text-slate-400" />
                                    <p className="text-xs text-slate-500">{activeCompany.name}</p>
                                    {activeCompany.role && (
                                        <span className={`text-xs px-1 py-0.5 rounded ${getRoleBadgeClass(activeCompany.role)}`}>
                                            {activeCompany.role}
                                        </span>
                                    )}
                                </div>
                            )}
                            {/* Affinity Programme badge — company-based; only
                                rendered when the active company is a member
                                of an ACTIVE programme. Never says "Logged in
                                with X" — the programme belongs to the
                                COMPANY, not the user. */}
                            {activeCompany?.affinity_programme?.name && (
                                <div
                                    className="flex items-center gap-1.5 mt-1"
                                    data-testid="active-company-affinity-programme"
                                >
                                    <Sparkles className="h-3 w-3 text-emerald-500" />
                                    <p className="text-xs text-emerald-700">
                                        {t('header.affinity_programme', 'Affinity Programme')} ·{' '}
                                        <span className="font-medium">
                                            {activeCompany.affinity_programme.name}
                                        </span>
                                    </p>
                                </div>
                            )}
                        </div>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-xs text-muted-foreground cursor-default">
                            <User className="mr-2 h-3 w-3" />
                            {t('header.role_label', 'Role:')} {user?.role}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                            onClick={() => navigate('/profile')} 
                            className="cursor-pointer"
                            data-testid="profile-link"
                        >
                            <User className="mr-2 h-4 w-4" />
                            {t('header.profile_notifications', 'Profile & Notifications')}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                            onClick={() => navigate('/billing')} 
                            className="cursor-pointer"
                            data-testid="billing-profile-link"
                        >
                            <CreditCard className="mr-2 h-4 w-4" />
                            {t('header.billing_profile', 'Billing Profile')}
                            {billingWarning && (
                                <AlertTriangle className={`ml-auto h-4 w-4 ${billingStatus?.billing_status === 'pending_approval' ? 'text-blue-500' : 'text-amber-500'}`} />
                            )}
                        </DropdownMenuItem>
                        {hasManageableCompanies && (
                            <DropdownMenuItem
                                onClick={() => navigate('/team')}
                                className="cursor-pointer"
                                data-testid="team-link"
                            >
                                <Users className="mr-2 h-4 w-4" />
                                {t('header.team', 'Team')}
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {isAdmin && !isImpersonating && (
                            <>
                                <DropdownMenuItem 
                                    onClick={() => setShowImpersonateModal(true)} 
                                    className="cursor-pointer text-amber-600"
                                    data-testid="view-as-menu-item"
                                >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View as...
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        <DropdownMenuItem 
                            onClick={logout} 
                            className="text-red-600 cursor-pointer"
                            data-testid="logout-button"
                        >
                            <LogOut className="mr-2 h-4 w-4" />
                            {t('header.logout', 'Logout')}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Impersonate Modal */}
            <ImpersonateModal 
                open={showImpersonateModal} 
                onOpenChange={setShowImpersonateModal} 
            />
        </header>
    );
};
